import React, { useState } from 'react';
import { Box, Stack, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import Card from 'dashboard/components/card/Card.js';
import { GlassButton } from 'dashboard/components/common';

/**
 * Danger zone for a BRAND — not for the person signed in.
 *
 * Two things were wrong here. It said "Delete this account", which is the
 * user's account, not the brand being edited; they are separate objects and
 * the dashboard should never blur them. And the button had no `onClick` at
 * all, so the most destructive control on the page did nothing.
 *
 * The wording is "deactivate" because that is what the endpoint does — it
 * sets `is_active: false`. The brand, its orders and its catalogue are kept;
 * the storefront starts serving the Coming Soon page. Calling that "delete"
 * would promise an erasure that does not happen.
 *
 * Typing the brand name is required: this takes a live storefront offline,
 * and a single mis-click should not be enough.
 */
export default function Delete({ brandData }) {
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  const brandName = brandData?.name || '';
  const brandId = brandData?._id;
  const matches = confirmText.trim() === brandName.trim() && brandName.length > 0;

  const deactivate = () => {
    setBusy(true);
    API.deactivateBrand({ brand_id: brandId })
      .then(() => {
        toast.success(`${brandName} has been deactivated.`);
        setConfirmText('');
        // Full reload: the brand list, the switcher and the sidebar all cache
        // this brand, and a deactivated one should not stay on screen.
        window.location.assign('/dashboard/brand/overview');
      })
      .catch((err) => toast.error(err?.message || 'Could not deactivate this brand'))
      .finally(() => setBusy(false));
  };

  return (
    <Card sx={{ p: '30px', py: '28px', border: '1px solid', borderColor: 'error.light' }}>
      <Stack direction='column' spacing='16px'>
        <Box>
          <Typography variant='h6' fontWeight='bold' color='error.main'>
            Deactivate this brand
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: '4px' }}>
            {brandName ? <>“{brandName}”</> : 'This brand'} stops serving its storefront —
            visitors see the Coming Soon page instead. Products, orders and customers are
            kept, and support can switch it back on. Your own user account is not affected.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing='12px' alignItems={{ md: 'center' }}>
          <TextField
            size='small'
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={brandName || 'Brand name'}
            label={`Type “${brandName}” to confirm`}
            disabled={!brandId || busy}
            sx={{ flex: 1, maxWidth: 360 }}
          />
          <GlassButton
            variant='outlined'
            color='error'
            onClick={deactivate}
            disabled={!matches || busy || !brandId}
            disabledReason={
              !brandId
                ? 'No brand loaded yet.'
                : !matches
                  ? `Type the brand name exactly — “${brandName}” — to confirm.`
                  : ''
            }
            sx={{ height: '44px', px: '28px', fontWeight: 500, whiteSpace: 'nowrap' }}
          >
            {busy ? 'Deactivating…' : 'Deactivate brand'}
          </GlassButton>
        </Stack>
      </Stack>
    </Card>
  );
}
